/**
 * Products — the catalog with real sales (price/units/revenue from orders).
 * Concentration + units-per-product roll up by the founder's product groups
 * (the Organize-catalog overlay) when set; otherwise they fall back to raw SKU
 * names with a nudge to organize.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Segmented } from "../components/Segmented.js";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod } from "../lib/period.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { Tile, Card, CardHead, Explain, moneyFmt } from "../components/cards.js";
import { RankedBars } from "../components/RankedBars.js";
import { useDrill } from "../lib/drill.js";
import {
  BuyGoodsProductMetricUnavailable,
  ProductTable,
  ProductRevenueTrendCard,
  num,
  useProductCapabilities,
  useProductsSpark,
  type ProductRow,
} from "../cards/products.js";
import { GatewayProductsCard, BoughtTogetherCard, ProductMoversCard } from "../cards/gateway-products.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "../components/RevenueCmToggle.js";
import { useCmReady } from "../lib/use-cm-ready.js";
import { productSkuLabel } from "../lib/product-sku-label.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface SummaryRow {
  groupId: string;
  groupName: string;
  skuCount: number;
  boxes: number;
  units: number;
  revenue: number;
  cm: number | null;
  orders: number;
}
interface CatalogSettings {
  unitLabel: string;
  configuredAt: string | null;
}

/** Aggregate the raw catalog by SKU productName → metric. Fallback path used
 *  until the founder has organized SKUs into product groups. */
function byProduct(products: ProductRow[], metric: "revenue" | "cm" | "units" | "boxes"): { key: string; value: number }[] {
  const m = new Map<string, number>();
  for (const p of products) m.set(p.productName, (m.get(p.productName) ?? 0) + (p[metric] ?? 0));
  return [...m.entries()].map(([key, value]) => ({ key, value })).sort((a, b) => b.value - a.value);
}

/** Same concentration, one slice per SKU (founder 2026-06-26: SKU-level everywhere). */
function bySku(products: ProductRow[], metric: "revenue" | "cm" | "units" | "boxes"): { key: string; value: number }[] {
  const m = new Map<string, number>();
  for (const p of products) {
    const label = productSkuLabel(p);
    const key = `${label.description}${label.details ? ` · ${label.details}` : ""} · ${label.code}`;
    m.set(key, (m.get(key) ?? 0) + (p[metric] ?? 0));
  }
  return [...m.entries()].map(([key, value]) => ({ key, value })).sort((a, b) => b.value - a.value);
}

const TOTAL_UNITS_EXPLAIN = [
  "<b>What it is:</b> The individual units that went out the door. A 6-pack counts as 6. A separate sample discounted to $0 also counts because it shipped.",
  "<b>How it's calculated:</b> Multiply each real catalog line by its pack size, then add the results. Literal $0 rows that only list the bottles inside a paid pack stay out, so those bottles are never counted twice.",
  "<b>Example (sample data · year-to-date):</b> Daily Greens 90ct sells in 3-packs, so its 2,476 sales = 7,428 units; Deep Sleep 99 (3-packs) = 5,760; Radiant Collagen 80 (2-packs) = 1,448; everything else single. Total = 24,348 individual units.",
  "<b>Note:</b> Pick a product to narrow this. A separate physical sample counts, even when it is free. A $0 bookkeeping row for units already inside a paid pack does not count again. Switch to <b>By product</b> to see the split.",
];

/** Total units — with a Total / By-product toggle (founder 2026-07-07: "if I have
 *  the number it shows the total; if I have the table it shows units by product").
 *  Total mode is the normal Tile (keeps its trend + drill); By-product mode swaps the
 *  number for a compact, scrollable product→units table. */
function TotalUnitsTile({ rows, total, spark, periodLabel }: {
  rows: ProductRow[]; total: number; spark?: number[]; periodLabel: string;
}): React.ReactElement {
  const [mode, setMode] = useState<"total" | "byProduct">("total");
  const toggle = (
    <Segmented size="sm" ariaLabel="Units sold view" value={mode} onChange={setMode}
      options={[{ value: "total", label: "Total" }, { value: "byProduct", label: "By product" }]} />
  );
  if (mode === "total") {
    return <Tile title={`Units sold · ${periodLabel}`} value={num(total)} sub="a 6-pack counts as 6" spark={spark}
      drill={{ title: "Units sold", metric: "boxes", dimension: "month" }} explain={TOTAL_UNITS_EXPLAIN} headerAction={toggle} />;
  }
  const byProd = byProduct(rows, "boxes").filter((r) => r.value > 0);
  return (
    <Card style={{ marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ color: "var(--text)", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, opacity: 0.85 }}>
          {`Units sold · ${periodLabel}`}
          <span onClick={(e) => e.stopPropagation()}><Explain title="Units sold" lines={TOTAL_UNITS_EXPLAIN} /></span>
        </span>
        {toggle}
      </div>
      <div style={{ marginTop: 12, maxHeight: 188, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {byProd.length === 0 && <span style={{ color: "var(--faint)", fontSize: 12 }}>No units sold in this period.</span>}
        {byProd.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", borderBottom: "1px solid var(--hair)" }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.key}>{r.key}</span>
            <span className="dd-num" style={{ width: 70, textAlign: "right", fontSize: 13, fontWeight: 600, color: "var(--num)" }}>{num(r.value)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function ProductsPage(): React.ReactElement {
  const period = usePeriod();
  const api = useApiClient();
  const navigate = useNavigate();
  const demo = useDemoMode();
  const drill = useDrill();
  const hasCm = useCmReady();
  const caps = useProductCapabilities();
  const quantityReady = caps.data?.physicalQuantity === true;
  const customerValueReady = caps.data?.grossCustomerValue === true;
  const detailedSourceGap = caps.data?.buyGoodsDetailedHistoryGap === true;
  const [concentrationMode, setConcentrationMode] = useStickyRevenueCmMode("dd-mode-products-concentration");
  const concentrationCm = concentrationMode === "profit" && hasCm;

  const productsQ = useQuery({
    queryKey: ["products", period.cacheKey],
    queryFn: () => api.get<{ products: ProductRow[] }>("/products", { ...period.query }),
  });
  const data = productsQ.data;
  // Grouped totals from the categorization overlay (boxes account for pack/offer).
  const summary = useQuery({
    queryKey: ["product-summary", period.cacheKey],
    queryFn: () => api.get<{ rows: SummaryRow[] }>("/catalog/product-summary", { ...period.query }),
  });
  const settingsQ = useQuery({
    queryKey: ["catalog-settings"],
    queryFn: () => api.get<{ settings: CatalogSettings | null }>("/catalog/settings"),
  });

  const unitsSpark = useProductsSpark("units", quantityReady); // paid products sold / month
  const boxesSpark = useProductsSpark("boxes", quantityReady); // pack contents / month
  const productRows = data?.products ?? [];
  // "Products sold" = how many products went out the door (a multi-pack = 1).
  // "Total units" = the individual units inside that (a 6-pack = 6 = pack contents).
  const productsSold = productRows.reduce((s, r) => s + r.units, 0);
  const totalUnits = productRows.reduce((s, r) => s + r.boxes, 0);

  const unitLabel = settingsQ.data?.settings?.unitLabel || "units";
  const summaryRows = summary.data?.rows ?? [];
  const organized = summaryRows.some((r) => !r.groupId.startsWith("unclassified:"));

  // Both donuts share a SKUs/Products view (founder 2026-06-26). SKU mode breaks the
  // concentration down to individual SKUs; Products rolls up to families (the founder's
  // groups when organized, else raw product names).
  const [donutGroup, setDonutGroup] = useState<"sku" | "product">("product");
  const sku = donutGroup === "sku";
  const prods = data?.products ?? [];
  const concentrationMetric = concentrationCm ? "cm" : "revenue";
  const revenueSlices = sku
    ? bySku(prods, concentrationMetric)
    : organized
      ? summaryRows.map((r) => ({ key: r.groupName, value: r[concentrationMetric] ?? 0 })).sort((a, b) => b.value - a.value)
      : byProduct(prods, concentrationMetric);
  // Individual units both ways (a 6-pack = 6 = pack contents), so the donut stops
  // flipping its unit basis on the organize toggle and matches the "Total units"
  // tile (founder 2026-06-25: units = individual units).
  const unitSlices = (sku
    ? bySku(prods, "boxes")
    : organized
      ? summaryRows.map((r) => ({ key: r.groupName, value: r.boxes }))
      : byProduct(prods, "boxes")
  ).sort((a, b) => b.value - a.value);
  const donutToggle = (
    <Segmented ariaLabel="Donut grouping" size="sm" value={donutGroup} onChange={setDonutGroup}
      options={[{ value: "sku", label: "SKUs" }, { value: "product", label: "Products" }]} />
  );
  const openConcentration = (key: string, metric: "product_revenue" | "product_cm" | "boxes"): void => {
    const product = sku ? prods.find((p) => bySku([p], "revenue")[0]?.key === key) : undefined;
    drill.open({
      title: `${key} · ${metric === "boxes" ? "Individual units" : metric === "product_cm" ? "Product margin" : "Product revenue before refunds"}`,
      metric,
      dimension: "month",
      grouping: sku ? "sku" : "product",
      filters: sku ? { product: product?.skuCode ?? key } : organized ? { productGroup: key } : { product: key },
    });
  };
  const pageLoading = productsQ.isLoading || summary.isLoading;
  const requiredLoad = pageLoadState(caps, productsQ, summary);

  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your products"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }

  return (
    <div>
      <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Catalog</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Products</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 760, lineHeight: 1.5, marginTop: 0 }}>
        {quantityReady
          ? "Products sold or shipped during these dates, with price, units, and revenue pulled straight from your real orders."
          : "Products sold during these dates, with price, revenue, and order count pulled straight from your real orders."}
      </p>
      {!quantityReady && caps.data !== undefined && (
        <p style={{ color: "var(--body)", fontSize: 12.5, margin: "-2px 0 14px" }}>
          Physical unit counts aren't included in this order history, so this page stays focused on product revenue and orders.
        </p>
      )}

      {pageLoading && <p role="status" style={{ marginTop: 20 }}>Loading…</p>}

      {data && summary.data && (
        <SortableArea pageKey="products">
          {quantityReady && <Hideable id="products-sold" label="Products sold" span={6}><Tile title={`Products sold · ${period.label}`} value={num(productsSold)} sub="a multi-pack counts as 1" spark={unitsSpark} drill={{ title: "Products sold by month", metric: "units", dimension: "month" }} explain={[
            "<b>What it is:</b> How many products customers actually bought, where a multi-pack counts as one item and free $0 giveaway items don't count at all.",
            "<b>How it's calculated:</b> Add up every product's paid sale-units across your catalog (a $0 bonus or BOGO half is excluded).",
            "<b>Example (sample data · year-to-date):</b> Daily Greens 90ct 2,476 + Deep Sleep 99 1,920 + Daily Greens 30ct 2,386 + Omega Gold 2,221 + the rest = 17,262 products sold.",
            "<b>Note:</b> A product filter narrows this. The 7-Day Reset Guide shows 0 because it's always a $0 bonus — free items are deliberately excluded even though it rides along on 1,588 orders.",
          ]} /></Hideable>}
          {quantityReady && <Hideable id="total-units" label="Units sold" span={6}><TotalUnitsTile rows={productRows} total={totalUnits} spark={boxesSpark} periodLabel={period.label} /></Hideable>}

          <Hideable id="product-revenue-trend" label="Product revenue over time" span={12}><ProductRevenueTrendCard /></Hideable>

          {!organized && !demo && hasCm && (
            <div data-card-id="organize-banner" data-span={12} style={organizeBanner} onClick={() => navigate("/onboarding/chat")} className="dd-clickable-card" title="Set up products & margin">
              <span>
                These views group by raw SKU name. <strong>Organize your catalog</strong> to roll SKUs up into real products and offers.
                {quantityReady ? <> Then count them in {unitLabel}.</> : <> Physical quantity stays unavailable until it is added from a source.</>}
              </span>
              <span style={{ color: "var(--accent)", fontWeight: 600, whiteSpace: "nowrap" }}>Organize catalog →</span>
            </div>
          )}

          <div data-card-id="concentration-toggle" data-span={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12.5, color: "var(--body)" }}>Concentration by</span>{donutToggle}
          </div>
          <Hideable id="revenue-concentration" label="Revenue concentration" span={6}>
            <Card style={{ marginBottom: 0 }}>
              <CardHead title={`How concentrated is ${concentrationCm ? "CM" : "revenue"}? · ${period.label}`} explain={[
                `<b>What it is:</b> Each ${sku ? "SKU" : "product"}'s share of revenue or contribution margin (CM). One dominant slice means the business depends heavily on that product.`,
                "<b>How it's calculated:</b> The selected products and dates are grouped by SKU or product. Revenue adds sales. CM uses the same items and subtracts each SKU's variable costs.",
                "<b>Example (sample data · year-to-date, Revenue):</b> Daily Greens is about 37% of product revenue, followed by Deep Sleep and Radiant Collagen.",
                "<b>Note:</b> Pick a product to narrow both views. CM turns on after every selling product has costs. These are item-level dollars, matching the product table.",
              ]}><RevenueCmToggle mode={concentrationCm ? "profit" : "revenue"} setMode={setConcentrationMode} cmDisabled={!hasCm} /></CardHead>
              <div style={{ marginTop: 12 }}>
                <p style={{ color: "var(--body)", fontSize: 12, margin: "0 0 8px" }}>{concentrationCm ? "Product margin before refunds." : "Product revenue before refunds. Shipping and tax are not included."}</p>
                <RankedBars rows={revenueSlices} fmt={moneyFmt} donutToggle defaultView="donut"
                  onPick={(key) => openConcentration(key, concentrationCm ? "product_cm" : "product_revenue")} />
              </div>
            </Card>
          </Hideable>
          {quantityReady && <Hideable id="units-per-product" label="Units per product" span={6}>
            <Card style={{ marginBottom: 0 }}>
              <CardHead title={`Units sold per ${sku ? "SKU" : "product"} · ${period.label}`} explain={[
                `<b>What it is:</b> Each ${sku ? "SKU" : organized ? "product" : "product"}'s share of individual units sold — the volume leaders, which aren't always the revenue leaders. A cheap high-volume product can quietly drive your fulfillment load.`,
                "<b>How it's calculated:</b> The same catalog, counting individual units (a 6-pack = 6) per SKU or per product family, sorted biggest-first.",
                "<b>Example (sample data · year-to-date, Products view):</b> Daily Greens family leads on units at 9,814 (a bigger unit-share than revenue-share, thanks to its 3-packs), then Deep Sleep 7,419, Radiant Collagen 3,117, Omega Gold 2,221, Gut Guardian 1,812. Total = 24,348 units.",
                "<b>Note:</b> Pick a product to narrow this. Separate physical samples count. $0 bookkeeping rows for units already inside a paid pack do not count again. Both views match the Units sold tile.",
              ]} />
              <div style={{ marginTop: 12 }}>
                <RankedBars rows={unitSlices} fmt={num} money={false} donutToggle defaultView="donut"
                  onPick={(key) => openConcentration(key, "boxes")} />
              </div>
            </Card>
          </Hideable>}

          <Hideable id="product-movers" label="What's rising & fading" span={6}><ProductMoversCard /></Hideable>
          <Hideable id="bought-together" label="Frequently bought together" span={6}><BoughtTogetherCard /></Hideable>

          <Hideable id="gateway-products" label="Which products create your best customers?" span={12}>{customerValueReady ? <GatewayProductsCard revenueOnly={!hasCm} /> : <BuyGoodsProductMetricUnavailable title="Which products create your best customers?" detail={detailedSourceGap ? "This source history does not connect customers, products, and recorded refunds reliably enough for this view." : "We could not confirm the source details needed for customer LTV."} />}</Hideable>

          <Hideable id="product-table" label="Product details" span={12}><ProductTable showQuantityNotice={false} /></Hideable>
        </SortableArea>
      )}
    </div>
  );
}


const organizeBanner: React.CSSProperties = {
  display: "flex",
  gap: 16,
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
  border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
  borderRadius: "var(--r-card)",
  padding: "12px 16px",
  marginBottom: 16,
  fontSize: 13.5,
  color: "var(--body)",
  cursor: "pointer",
};
